// ControlModeToggle.js
import { Box, Button, Stack } from '@mui/material';
import GlobalStyles from '@mui/material/GlobalStyles';
import { keyframes } from '@mui/material/styles';
import { brandColors } from 'types/colors';
const BTN_HEIGHT = 32;

const CONTAINER_HEIGHT = BTN_HEIGHT + 30;
const CONTAINER_MAX_W = 150; // ~ 3 buttons width
const MODES = ['solai', 'schedule'];
const rotate = keyframes`
  from { --border-angle: 0deg; }
  to   { --border-angle: 360deg; }
`;

const DcvMode = ({ dcvEnabled = false, setControl, dcvMode = 'solai' }) => {
  const disabled = dcvEnabled === false || dcvMode === 'manual';
  const ordered = MODES;
  const handleSelect = m => {
    if (disabled) return;
    setControl(m); // your manual modal guard runs here
  };

  const isSelected = m => m === dcvMode;

  const commonBtnSx = active => ({
    '--border-angle': '0deg',
    flex: 1,
    minWidth: 68,
    height: BTN_HEIGHT,
    p: 0,
    borderRadius: 2,
    border: t => `1px solid ${brandColors?.GREY_DARK || t.palette.divider}`,
    backgroundColor: t => (disabled ? t.palette.action.disabledBackground : active ? brandColors?.BLUE : brandColors?.GREY_LIGHT),
    color: t => (disabled ? 'red' : active ? '#fff' : brandColors?.BLACK),
    transition: 'transform 180ms ease, box-shadow 180ms ease, background-color 180ms ease',
    '&:hover': {
      // transform: disabled ? 'none' : 'translateY(-1px)',
      boxShadow: disabled ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.32)',
    },
  });

  const selectedSolaiSx = {
    border: '2px solid transparent',
    background: `linear-gradient(${brandColors.BLUE}, ${brandColors.BLUE}) padding-box,
       conic-gradient(
         from var(--border-angle),
         ${brandColors.BLACK},
         ${brandColors.BLUE},
         ${brandColors.TURQUOISE},
         #fff,
         ${brandColors.BLACK}
       ) border-box`,
    animation: `${rotate} 3s linear infinite`,
  };

  const labelFor = m => {
    if (m === 'solai') {
      const white = `${process.env.PUBLIC_URL}/solaiwhite.png`;
      const dark = `${process.env.PUBLIC_URL}/solaidark.png`;
      const src = dcvMode === 'solai' ? white : dark;
      return (
        <Box
          component="img"
          src={src}
          alt="Sol AI"
          sx={{ width: '100%', height: '100%', objectFit: 'contain', transform: 'scale(.7)' }}
        />
      );
    }
    return 'SCHEDULE';
  };

  return (
    <>
      {dcvMode === 'solai' && (
        <GlobalStyles
          styles={`
            @property --border-angle {
              syntax: "<angle>";
              initial-value: 0deg;
              inherits: false;
            }
          `}
        />
      )}
      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        sx={{
          ml: 1.5,
        }}>
        {/* Faded container with the three buttons inside */}
        <Box
          sx={{
            position: 'relative',
            height: CONTAINER_HEIGHT,
            maxWidth: CONTAINER_MAX_W,
            display: 'flex',
            alignItems: 'center',
            // The container background so the fades blend correctly
            backgroundColor: '#ffffff00',
            borderRadius: 2,
            px: 1, // a little inner padding
            justifyContent: 'center',
          }}>
          {/* Buttons row */}
          <Stack
            direction="row"
            spacing={1}
            sx={{
              alignItems: 'center',
              position: 'relative',
              zIndex: 1, // below fades but above container

              // small emphasis for the center (selected) item
              '& > *:nth-of-type(2)': { transform: 'scale(1.02)' },
              '& > *': { transition: 'transform 180ms ease, opacity 180ms ease' },
            }}>
            {ordered.map((m, i) => (
              <Button
                color="inherit"
                key={`${m}-${i}`}
                disabled={disabled}
                onClick={() => handleSelect(m)}
                sx={{
                  ...commonBtnSx(isSelected(m)),
                  ...(m === 'solai' && isSelected(m) ? selectedSolaiSx : {}),
                  fontSize: m === 'manual' ? '.85rem' : '.75rem',
                  '&:hover': {
                    color: brandColors.BLUE,
                    boxShadow: '0px 0px 6px 1px rgba(0,0,0,0.3);',
                  },
                }}
                aria-pressed={isSelected(m)}
                aria-label={`${m} mode`}>
                {labelFor(m)}
              </Button>
            ))}
          </Stack>
        </Box>
      </Stack>
    </>
  );
};

export default DcvMode;
